<script setup lang="ts">
// Types
import { WizardProjectType } from "@/features/BuildingProject";

// Utils
import { referenceAreaApplies } from "@/features/BuildingProject/utils/wizardStepConditions";

// Components
import BuildingDetails from "./BuildingDetails.vue";
import ReferenceArea from "../ReferenceArea/ReferenceArea.vue";
import StepIntro from "../StepIntro/StepIntro.vue";

/**
 * The create wizard's Building step, with reference areas folded into it.
 *
 * Reference area was a step of its own, and its top half was a restatement of the step before it:
 * `DisabledAreaInputs` is three read-only totals computed from the areas just typed on Building. A screen
 * whose first act is to show you your own previous answers, uneditable, is not a step. What it genuinely
 * asks — the optional "additional reference areas" rows — belongs under the areas it adds to.
 *
 * Create only. The edit wizard keeps both as separate steps, and can, because the two flows have separate
 * registries (`createWizardSteps.ts` and `settingsSections.ts`). Both children carry their own card styling
 * unconditionally, the same precedent ProjectDetails' cards set: the edit wizard renders the same
 * "Above ground" / "Below ground" cards on its own Building step, and the same "Reference areas" card one
 * screen later, on its own Reference area step.
 *
 * The two halves are not symmetrical: Building is in every country's flow, reference areas are not. That
 * condition used to be implicit in which flow arrays listed the step and now lives in
 * `referenceAreaApplies`, which `createWizardSteps`' `valid()` reads too. One predicate, two readers: if the
 * `v-if` here and that `valid()` ever disagreed, the step would block on rows nobody can see.
 */
const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const showReferenceArea = () => referenceAreaApplies(props.project);
</script>

<template>
  <div class="w-full">
    <!-- One heading for the screen, so the halves read as sections of a question rather than two steps.
         Its wording covers both: the areas, and what the result is measured against. -->
    <StepIntro
      class="mb-6"
      heading="How big is the building?"
      hint="Gross area above and below ground, how much of that is heated, and the areas the result is measured against." />

    <!-- gap-3 rather than a divider between the two rows of cards: each half already reads as its own
         section by its card borders, so a hairline rule between them would only repeat that. -->
    <div class="flex w-full flex-col gap-3">
      <BuildingDetails
        :project="project"
        :was-validated="wasValidated"
        :hide-intro="true" />

      <ReferenceArea
        v-if="showReferenceArea()"
        :project="project"
        :was-validated="wasValidated"
        :hide-intro="true" />
    </div>
  </div>
</template>
